import { useState, useEffect, useCallback } from 'react';
import { newsAPI, CompanySummary } from '../lib/supabase';

interface CacheEntry {
  data: CompanySummary;
  timestamp: number;
  loading: boolean;
}

interface CacheState {
  [companyId: string]: CacheEntry;
}

const CACHE_DURATION = 30 * 60 * 1000; // 30分钟缓存，减少重新请求
const MAX_CACHE_SIZE = 300; // 增加缓存大小到300个公司
const PRELOAD_DEBOUNCE_TIME = 300; // 预加载防抖时间（减少到300ms）
const SESSION_STORAGE_KEY = 'fomo_company_cache'; // 会话存储key
const CACHE_VERSION = 'v2'; // 缓存版本，用于失效旧缓存

export const useCompanySummaryCache = () => {
  const [cache, setCache] = useState<CacheState>(() => {
    // 初始化时从sessionStorage加载缓存
    try {
      const savedData = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (savedData) {
        const parsed = JSON.parse(savedData);
        // 检查缓存版本
        if (parsed.version !== CACHE_VERSION) {
          // 版本不匹配，清空旧缓存
          sessionStorage.removeItem(SESSION_STORAGE_KEY);
          return {};
        }
        
        const parsedCache = parsed.cache || {};
        // 验证缓存是否过期
        const validCache: CacheState = {};
        const now = Date.now();
        Object.entries(parsedCache).forEach(([companyId, entry]) => {
          const cacheEntry = entry as CacheEntry;
          if (now - cacheEntry.timestamp < CACHE_DURATION) {
            validCache[companyId] = { ...cacheEntry, loading: false };
          }
        });
        return validCache;
      }
    } catch (error) {
      console.warn('Failed to load cache from sessionStorage:', error);
      // 清理损坏的缓存
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
    return {};
  });
  const [loadingStates, setLoadingStates] = useState<{ [key: string]: boolean }>({});

  // 保存缓存到sessionStorage
  const saveCacheToSession = useCallback((cacheState: CacheState) => {
    try {
      // 只保存数据，不保存loading状态
      const cacheToSave: CacheState = {};
      Object.entries(cacheState).forEach(([companyId, entry]) => {
        cacheToSave[companyId] = {
          data: entry.data,
          timestamp: entry.timestamp,
          loading: false
        };
      });
      const dataToSave = {
        version: CACHE_VERSION,
        cache: cacheToSave,
        savedAt: Date.now()
      };
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(dataToSave));
    } catch (error) {
      console.warn('Failed to save cache to sessionStorage:', error);
      // 如果存储失败（可能是配额超限），尝试清理一些旧缓存
      try {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      } catch {
        // 忽略清理错误
      }
    }
  }, []);

  // 检查缓存是否有效
  const isCacheValid = useCallback((entry: CacheEntry): boolean => {
    return Date.now() - entry.timestamp < CACHE_DURATION;
  }, []);

  // 清理过期缓存
  const cleanupCache = useCallback(() => {
    setCache(prevCache => {
      const now = Date.now();
      const validEntries: CacheState = {};

      Object.entries(prevCache).forEach(([companyId, entry]) => {
        if (now - entry.timestamp < CACHE_DURATION) {
          validEntries[companyId] = entry;
        }
      });

      // 如果缓存太大，保留最新的条目
      const entries = Object.entries(validEntries);
      let finalEntries = validEntries;
      
      if (entries.length > MAX_CACHE_SIZE) {
        entries.sort((a, b) => b[1].timestamp - a[1].timestamp);
        const limitedEntries: CacheState = {};
        entries.slice(0, MAX_CACHE_SIZE).forEach(([id, entry]) => {
          limitedEntries[id] = entry;
        });
        finalEntries = limitedEntries;
      }

      // 保存清理后的缓存
      saveCacheToSession(finalEntries);
      return finalEntries;
    });
  }, [saveCacheToSession]);

  // 定期清理缓存
  useEffect(() => {
    const interval = setInterval(cleanupCache, 5 * 60 * 1000); // 每5分钟清理一次，减少清理频率
    return () => clearInterval(interval);
  }, [cleanupCache]);

  // 获取公司总结（带缓存）
  const getCompanySummary = useCallback(
    async (companyId: string): Promise<CompanySummary> => {
      // 检查缓存
      const cachedEntry = cache[companyId];
      if (cachedEntry && isCacheValid(cachedEntry) && !cachedEntry.loading) {
        return cachedEntry.data;
      }

      // 如果正在加载，等待加载完成
      if (loadingStates[companyId]) {
        return new Promise((resolve, reject) => {
          const checkLoading = () => {
            const entry = cache[companyId];
            if (entry && !entry.loading) {
              resolve(entry.data);
            } else if (!loadingStates[companyId]) {
              reject(new Error('Loading cancelled'));
            } else {
              setTimeout(checkLoading, 100);
            }
          };
          checkLoading();
        });
      }

      // 开始加载
      setLoadingStates(prev => ({ ...prev, [companyId]: true }));

      try {
        const data = await newsAPI.getCompanySummary(companyId);

        // 更新缓存
        const newCache = {
          ...cache,
          [companyId]: {
            data,
            timestamp: Date.now(),
            loading: false,
          },
        };
        
        setCache(newCache);
        saveCacheToSession(newCache);

        setLoadingStates(prev => ({ ...prev, [companyId]: false }));
        return data;
      } catch (error) {
        setLoadingStates(prev => ({ ...prev, [companyId]: false }));
        throw error;
      }
    },
    [cache, loadingStates, isCacheValid, saveCacheToSession]
  );

  // 预加载公司总结
  const preloadCompanySummary = useCallback(
    async (companyId: string): Promise<void> => {
      // 如果已经有有效缓存或正在加载，跳过
      const cachedEntry = cache[companyId];
      if ((cachedEntry && isCacheValid(cachedEntry)) || loadingStates[companyId]) {
        return;
      }

      try {
        await getCompanySummary(companyId);
      } catch (error) {
        // 预加载失败不影响用户体验，静默处理
        console.warn(`Preload failed for company ${companyId}:`, error);
      }
    },
    [cache, loadingStates, isCacheValid, getCompanySummary]
  );

  // 智能批量预加载 - 防抖和去重
  const preloadMultiple = useCallback(
    async (companyIds: string[]): Promise<void> => {
      // 过滤出需要预加载的公司ID
      const needsPreload = companyIds.filter(id => {
        const cachedEntry = cache[id];
        return !loadingStates[id] && (!cachedEntry || !isCacheValid(cachedEntry));
      });

      if (needsPreload.length === 0) return;

      // 防抖处理：延迟执行预加载
      setTimeout(async () => {
        try {
          // 批量预加载，最多同时5个
          const batchSize = 5;
          for (let i = 0; i < needsPreload.length; i += batchSize) {
            const batch = needsPreload.slice(i, i + batchSize);
            const promises = batch.map(id => preloadCompanySummary(id));
            await Promise.allSettled(promises);

            // 已处理的项目会自动被缓存机制处理
          }
        } catch (error) {
          console.warn('Batch preload failed:', error);
        }
      }, PRELOAD_DEBOUNCE_TIME);
    },
    [cache, loadingStates, isCacheValid, preloadCompanySummary]
  );

  // 检查是否有缓存数据
  const hasCachedData = useCallback(
    (companyId: string): boolean => {
      const entry = cache[companyId];
      return entry ? isCacheValid(entry) : false;
    },
    [cache, isCacheValid]
  );

  // 检查是否正在加载
  const isLoading = useCallback(
    (companyId: string): boolean => {
      return loadingStates[companyId] || false;
    },
    [loadingStates]
  );

  // 清除特定公司的缓存
  const clearCache = useCallback((companyId?: string) => {
    if (companyId) {
      setCache(prev => {
        const newCache = { ...prev };
        delete newCache[companyId];
        return newCache;
      });
    } else {
      setCache({});
    }
  }, []);

  return {
    getCompanySummary,
    preloadCompanySummary,
    preloadMultiple,
    hasCachedData,
    isLoading,
    clearCache,
    cacheSize: Object.keys(cache).length,
  };
};
